<template>
    <div>
        <filter-ui ref="paymentFilter" :api-base-url="apiBaseUrl" :fields="filterFields" relatedTo="payment" :filter="initialFilter" v-on:change="filterChanged"></filter-ui>
        <ejs-grid id="paymentsGrid"
                  ref="paymentsGrid"
                  :dataSource="gridData"
                  :allowSorting="true"
                  :sortSettings="sortSettings"
                  :toolbar="toolbar"
                  :allowGrouping="false"
                  :groupSettings="groupSettings"
                  :allowResizing="true"
                  :allowPaging="true"
                  :pageSettings="pageSettings"
                  :showColumnChooser="true"
                  :allowExcelExport="true"
                  :dataStateChange="gridDataStateChanged"
                  :queryCellInfo="customiseCell"
                  :rowSelected="rowSelectionChanged"
                  :rowDeselected="rowSelectionChanged"
                  :toolbarClick="toolbarClicked"
                  :detailTemplate="detailTemplate"
                  :created="gridCreated"
                  :headerCellInfo="headerCellInfoEvent"
                  :enableStickyHeader="true">
            <e-columns>
                <e-column field="reference" headerText="Reference #" width="100"></e-column>
                <e-column field="otherReference" headerText="Order Reference #"></e-column>
                <e-column field="orderId" headerText="Order #"></e-column>
                <e-column field="clientOrderReference" headerText="Client Ref"></e-column>
                <e-column field="courtRegistryName" headerText="Court/Tribunal/Other"></e-column>
                <e-column field="interviewOrganisationName" headerText="Organisation"></e-column>
                <e-column field="status" headerText="Status"></e-column>
                <e-column field="amount" headerText="Amount" textAlign="Right" :allowSorting="false" :allowGrouping="false"></e-column>
                <e-column field="bankCharges" headerText="Bank Charges" textAlign="Right" :allowSorting="false" :allowGrouping="false"></e-column>
                <e-column field="gstPercentage" :headerText='$t("Shared.Label.GST")' textAlign="Right" :allowSorting="false" :allowGrouping="false"></e-column>
                <e-column field="gstAmount" :headerText='$t("Shared.Label.GST") + " Amount"' textAlign="Right" :allowSorting="false" :allowGrouping="false"></e-column>
                <e-column field="totalAmount" headerText="Total Amount" textAlign="Right"></e-column>
                <e-column field="createdDateTimeUTC" headerText="Created/Requested"></e-column>
                <e-column field="createdByUserAccount" headerText="Created By" :allowSorting="false" :allowGrouping="false"></e-column>
                <e-column field="expiryDateTimeUTC" headerText="Expiration"></e-column>
                <e-column field="payerEmail" headerText="Client Email"></e-column>
                <e-column field="paymentSubmissionReference" headerText="Submission Ref #" :allowSorting="false" :allowGrouping="false"></e-column>
                <e-column field="submittedDateTimeUTC" headerText="Submitted"></e-column>
                <e-column field="processedOrReceivedDateTimeUTC" headerText="Processed/Received"></e-column>
                <e-column field="refundedAmount" headerText="Refunded Amount" textAlign="Right" :allowSorting="false" :allowGrouping="false"></e-column>
            </e-columns>
        </ejs-grid>

        <validation-observer v-slot="vo" slim>
            <b-modal size="md"
                     centered
                     no-close-on-backdrop
                     no-close-on-esc
                     v-model="refundPaymentData.dialogOpen"
                     v-on:ok.prevent="vo.handleSubmit(refundPayment)">
                <template #modal-header>
                    <h5 class="modal-title">Refund</h5>
                </template>
                <template #default>
                    <b-alert :show="(refundPaymentData.currentErrors != null) && (refundPaymentData.currentErrors.length > 0)" variant="danger" dismissible>
                        <div v-for="(error, index) in refundPaymentData.currentErrors" :key="index">
                            <p>{{ error }}</p>
                        </div>
                    </b-alert>
                    <p>
                        Please specify the refund amount in the input field below.
                    </p>
                    <p v-if="refundPaymentData.gstPercentage && (refundPaymentData.gstPercentage > 0) && !refundPaymentData.amountIncludesGst">
                        The specified amount cannot be more than {{ formatAsCurrency(refundPaymentData.maxAmount, refundPaymentData.currencyCode) }} and has to be <u>exclusive</u> of {{ formatAsPercentage(refundPaymentData.gstPercentage, true) }} {{ $t("Shared.Label.GST") }}.
                    </p>
                    <p v-if="refundPaymentData.gstPercentage && (refundPaymentData.gstPercentage > 0) && refundPaymentData.amountIncludesGst">
                        The specified amount cannot be more than {{ formatAsCurrency(refundPaymentData.maxAmount, refundPaymentData.currencyCode) }} and has to be <u>inclusive</u> of {{ formatAsPercentage(refundPaymentData.gstPercentage, true) }} {{ $t("Shared.Label.GST") }}.
                    </p>
                    <p v-if="!refundPaymentData.gstPercentage || (refundPaymentData.gstPercentage <= 0)">
                        The specified amount cannot be more than {{ formatAsCurrency(refundPaymentData.maxAmount, refundPaymentData.currencyCode) }}.
                    </p>
                    <b-form-row>
                        <validation-provider :rules="refundPaymentData.validationRules.amount" name="Amount" v-slot="vp" slim 
                                             :custom-messages="refundPaymentData.customMessages.amount">
                            <b-form-group label="Amount"
                                          label-for="amount"
                                          class="col-md-6">
                                <currency-input v-model="refundPaymentData.amount"
                                                :locale="$config.currentLocale"
                                                :currency="refundPaymentData.currencyCode"
                                                :precision="2"
                                                :class="vp.errors[0] ? 'text-right form-control form-control is-invalid' : 'text-right form-control form-control'"
                                                :disabled="refundPaymentData.busy">
                                </currency-input>                                
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                    </b-form-row>
                </template>
                <template #modal-footer="{ ok, cancel }">
                    <button type="button" class="btn btn-secondary" @click="cancel()" :disabled="refundPaymentData.busy">Cancel</button>
                    <button type="button" class="btn btn-primary" @click="ok()" :disabled="refundPaymentData.busy">
                        <b-spinner small v-if="refundPaymentData.busy" label="Processing refund..."></b-spinner>
                        <template v-if="!refundPaymentData.busy">
                            Process
                        </template>
                    </button>
                </template>
            </b-modal>
        </validation-observer>

        <!-- new external payment start -->
        <validation-observer v-slot="vo" slim>
            <b-modal size="md"
                     centered
                     no-close-on-backdrop
                     no-close-on-esc
                     v-model="newExternalPaymentData.dialogOpen"
                     v-on:ok.prevent="vo.handleSubmit(newExternalPayment)">
                <template #modal-header>
                    <h5 class="modal-title">New Payment for Queensland Courts</h5>
                </template>
                <template #default>
                    <b-alert :show="(newExternalPaymentData.currentErrors != null) && (newExternalPaymentData.currentErrors.length > 0)" variant="danger" dismissible>
                        <div v-for="(error, index) in newExternalPaymentData.currentErrors" :key="index">
                            <p>{{ error }}</p>
                        </div>
                    </b-alert>
                    <p v-if="defaultExternalPaymentRequestorGstPercentage && (defaultExternalPaymentRequestorGstPercentage > 0) && !defaultExternalPaymentRequestorAmountIncludesGst">
                        The specified amounts have to be <u>exclusive</u> of {{ formatAsPercentage(defaultExternalPaymentRequestorGstPercentage, true) }} {{ $t("Shared.Label.GST") }}.
                    </p>
                    <p v-if="defaultExternalPaymentRequestorGstPercentage && (defaultExternalPaymentRequestorGstPercentage > 0) && defaultExternalPaymentRequestorAmountIncludesGst">
                        The specified amounts have to be <u>inclusive</u> of {{ formatAsPercentage(defaultExternalPaymentRequestorGstPercentage, true) }} {{ $t("Shared.Label.GST") }}.
                    </p>
                    <b-form-row>
                        <validation-provider rules="required"
                                             :custom-messages="newExternalPaymentData.customMessages.externalPaymentReference"
                                             name="Payment Reference" v-slot="vp" slim>
                            <b-form-group label="Payment Reference"
                                          label-for="externalPaymentReference"
                                          class="col-12">
                                <b-form-input name="externalPaymentReference"
                                              autofocus
                                              v-model="newExternalPaymentData.externalPaymentReference"
                                              :state="vp.errors[0] ? false : null"
                                              :disabled="newExternalPaymentData.busy">
                                </b-form-input>
                                <b-form-invalid-feedback>{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <b-form-group label="Order Reference"
                                      label-for="externalOtherReference"
                                      class="col-12">
                            <b-form-input name="externalOtherReference"
                                          v-model="newExternalPaymentData.externalOtherReference"
                                          :disabled="newExternalPaymentData.busy">
                            </b-form-input>
                        </b-form-group>
                        <validation-provider rules="email"
                                             :custom-messages="newExternalPaymentData.customMessages.payerEmail"
                                             name="Client Email" v-slot="vp" slim>
                            <b-form-group label="Client Email"
                                          label-for="payerEmail"
                                          class="col-12">
                                <b-form-input name="payerEmail"
                                              v-model="newExternalPaymentData.payerEmail"
                                              :state="vp.errors[0] ? false : null"
                                              :disabled="newExternalPaymentData.busy">
                                </b-form-input>
                                <b-form-invalid-feedback>{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider :rules="{ required: true, min_value: { min: 0.01 } }"
                                             :custom-messages="newExternalPaymentData.customMessages.amount"
                                             name="Amount" v-slot="vp" slim>
                            <b-form-group label="Amount"
                                          label-for="amount"
                                          class="col-md-6">
                                <currency-input v-model="newExternalPaymentData.amount"
                                                :locale="$config.currentLocale"
                                                :currency="$config.defaultCurrencyCode"
                                                :precision="2"
                                                :class="vp.errors[0] ? 'text-right form-control form-control is-invalid' : 'text-right form-control form-control'"
                                                :disabled="newExternalPaymentData.busy">
                                </currency-input>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider :rules="{ min_value: { min: 0.01 } }"
                                             :custom-messages="newExternalPaymentData.customMessages.totalAmount"
                                             name="Total Amount" v-slot="vp" slim>
                            <b-form-group label="Total Amount"
                                          label-for="amount"
                                          class="col-md-6">
                                <currency-input v-model="newExternalPaymentData.totalAmount"
                                                :locale="$config.currentLocale"
                                                :currency="$config.defaultCurrencyCode"
                                                :precision="2"
                                                :class="vp.errors[0] ? 'text-right form-control form-control is-invalid' : 'text-right form-control form-control'"
                                                :disabled="newExternalPaymentData.busy">
                                </currency-input>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <b-form-group label="Expiration Date"
                                      label-for="amount"
                                      class="col-md-6">
                            <b-form-datepicker :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                               placeholder="Undefined"
                                               locale="en-AU" right reset-button
                                               v-model="newExternalPaymentData.expiryDate"
                                               :disabled="newExternalPaymentData.busy">
                            </b-form-datepicker>
                        </b-form-group>
                    </b-form-row>
                </template>
                <template #modal-footer="{ ok, cancel }">
                    <button type="button" class="btn btn-secondary" @click="cancel()" :disabled="newExternalPaymentData.busy">Cancel</button>
                    <button type="button" class="btn btn-primary" @click="ok()" :disabled="newExternalPaymentData.busy">
                        <b-spinner small v-if="newExternalPaymentData.busy" label="Creating new payment..."></b-spinner>
                        <template v-if="!newExternalPaymentData.busy">
                            Create
                        </template>
                    </button>
                </template>
            </b-modal>
            <!-- new external payment end -->
            <!-- new generic payment start -->
            <b-modal size="md"
                     centered
                     no-close-on-backdrop
                     no-close-on-esc
                     v-model="newGenericPaymentData.dialogOpen"
                     v-on:ok.prevent="vo.handleSubmit(newGenericPayment)"
                     v-on:cancel="resetNewGenericPaymentData()">
                <template #modal-header>
                    <h5 class="modal-title">New Generic Payment</h5>
                </template>
                <template #default>
                    <b-alert :show="(newGenericPaymentData.currentErrors != null) && (newGenericPaymentData.currentErrors.length > 0)" variant="danger" dismissible>
                        <div v-for="(error, index) in newGenericPaymentData.currentErrors" :key="index">
                            <p>{{ error }}</p>
                        </div>
                    </b-alert>
                    <p v-if="defaultGenericPaymentRequestorGstPercentage && (defaultGenericPaymentRequestorGstPercentage > 0) && !defaultGenericPaymentRequestorAmountIncludesGst">
                        The specified amounts have to be <u>exclusive</u> of {{ formatAsPercentage(defaultGenericPaymentRequestorGstPercentage, true) }} {{ $t("Shared.Label.GST") }}.
                    </p>
                    <p v-if="defaultGenericPaymentRequestorGstPercentage && (defaultGenericPaymentRequestorGstPercentage > 0) && defaultGenericPaymentRequestorAmountIncludesGst">
                        The specified amounts have to be <u>inclusive</u> of {{ formatAsPercentage(defaultGenericPaymentRequestorGstPercentage, true) }} {{ $t("Shared.Label.GST") }}.
                    </p>
                    <b-form-row>

                        <b-form-group label="Court/Tribunal/Other"
                                      label-for="courtRegistryId"
                                      :class="(allInterviewOrganisations != null) && (allInterviewOrganisations.length > 0) ? 'col-md-6' : 'col'"
                                      v-if="(allCourtRegistries != null) && (allCourtRegistries.length > 0)">
                            <select2 name="courtRegistryId"
                                     key-property="courtRegistryId"
                                     text-property="name"
                                     in-b-modal
                                     v-model="newGenericPaymentData.courtRegistryId"
                                     :options="allCourtRegistries"
                                     @change="courtRegistryChanged">
                            </select2>
                        </b-form-group>

                        <b-form-group label="Organisation"
                                      label-for="interviewOrganisationId"
                                      :class="(allCourtRegistries != null) && (allCourtRegistries.length > 0) ? 'col-md-6' : 'col'"
                                      v-if="(allInterviewOrganisations != null) && (allInterviewOrganisations.length > 0)">
                            <select2 name="interviewOrganisationId"
                                     key-property="interviewOrganisationId"
                                     text-property="name"
                                     in-b-modal
                                     v-model="newGenericPaymentData.interviewOrganisationId"
                                     :options="allInterviewOrganisations"
                                     @change="interviewOrganisationChanged">
                            </select2>
                        </b-form-group>

                        <validation-provider rules="required"
                                             :custom-messages="newGenericPaymentData.customMessages.genericPaymentReference"
                                             name="Payment Reference" v-slot="vp" slim>

                            <b-form-group label="Payment Reference"
                                          label-for="genericPaymentReference"
                                          class="col-12">
                                <b-form-input name="genericPaymentReference"
                                              v-model="newGenericPaymentData.genericPaymentReference"
                                              :state="vp.errors[0] ? false : null"
                                              :disabled="newGenericPaymentData.busy">
                                </b-form-input>
                                <b-form-invalid-feedback>{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider rules="required"
                                             :custom-messages="newGenericPaymentData.customMessages.payerName"
                                             name="Client Name" v-slot="vp" slim>
                            <b-form-group label="Client Name"
                                          label-for="payerName"
                                          class="col-12">
                                <b-form-input name="payerName"
                                              v-model="newGenericPaymentData.payerName"
                                              :state="vp.errors[0] ? false : null"
                                              :disabled="newGenericPaymentData.busy">
                                </b-form-input>
                                <b-form-invalid-feedback>{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider rules="required|email"
                                             :custom-messages="newGenericPaymentData.customMessages.payerEmail"
                                             name="Client Email" v-slot="vp" slim>
                            <b-form-group label="Client Email"
                                          label-for="payerEmail"
                                          class="col-12">
                                <b-form-input name="payerEmail"
                                              v-model="newGenericPaymentData.payerEmail"
                                              :state="vp.errors[0] ? false : null"
                                              :disabled="newGenericPaymentData.busy">
                                </b-form-input>
                                <b-form-invalid-feedback>{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <b-form-group label="Matter or Case Reference"
                                      label-for="matterOrCaseReference"
                                      class="col-12">
                            <b-form-input name="matterOrCaseReference"
                                          v-model="newGenericPaymentData.matterOrCaseReference"
                                          :disabled="newGenericPaymentData.busy">
                            </b-form-input>
                        </b-form-group>

                        <b-form-group label="Description"
                                      label-for="description"
                                      class="col-12">
                            <b-form-textarea name="description"
                                             placeholder="Payment description"
                                             v-model="newGenericPaymentData.description"
                                             rows="3"
                                             max-rows="6"></b-form-textarea>
                        </b-form-group>

                        <validation-provider :rules="{ required: true, min_value: { min: 0.01 } }"
                                             :custom-messages="newGenericPaymentData.customMessages.amount"
                                             name="Amount" v-slot="vp" slim>
                            <b-form-group label="Amount"
                                          label-for="amount"
                                          class="col-md-6">
                                <currency-input v-model="newGenericPaymentData.amount"
                                                :locale="$config.currentLocale"
                                                :currency="$config.defaultCurrencyCode"
                                                :precision="2"
                                                :class="vp.errors[0] ? 'text-right form-control form-control is-invalid' : 'text-right form-control form-control'"
                                                :disabled="newGenericPaymentData.busy">
                                </currency-input>
                                <b-form-checkbox name="amountIncludesGst"
                                                 v-model="newGenericPaymentData.amountIncludesGst"
                                                 value="true"
                                                 unchecked-value="false"> Amount includes {{ $t("Shared.Label.GST") }}</b-form-checkbox>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <b-form-group label="Due Date"
                                      label-for="amount"
                                      class="col-md-6">
                            <b-form-datepicker :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                               placeholder="Undefined"
                                               locale="en-AU" right reset-button
                                               :min="new Date()"
                                               v-model="newGenericPaymentData.dueDateTimeUTC"
                                               :disabled="newGenericPaymentData.busy">
                            </b-form-datepicker>
                        </b-form-group>
                    </b-form-row>
                </template>
                <template #modal-footer="{ ok, cancel }">
                    <button type="button" class="btn btn-secondary" @click="cancel()" :disabled="newGenericPaymentData.busy">Cancel</button>
                    <button type="button" class="btn btn-primary" @click="ok()" :disabled="newGenericPaymentData.busy">
                        <b-spinner small v-if="newGenericPaymentData.busy" label="Creating new payment..."></b-spinner>
                        <template v-if="!newGenericPaymentData.busy">
                            Create
                        </template>
                    </button>
                </template>
            </b-modal>
            <!-- new generic payment end -->

        </validation-observer>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import { GridPlugin, ColumnChooser, Resize, Sort, Group, Page, Toolbar, Search, ExcelExport, DetailRow } from '@syncfusion/ej2-vue-grids';
    import { DataManager } from "@syncfusion/ej2-data";
    import axios from 'axios';

    import VueCurrencyInput from 'vue-currency-input'
    import { ValidationProvider, ValidationObserver } from 'vee-validate';

    import { extend, localize } from 'vee-validate';
    import { required, min_value, max_value, email } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    extend('min_value', min_value);
    extend('max_value', max_value);
    extend('email', email);
    localize('en', en);

    import PaymentsGridDetailsRow from './PaymentsGridDetailsRow';

    import FilterUI from './FilterUI';

    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import GridMixins from '../../mixins/GridMixins';
    import NumbersMixin from '../../mixins/NumbersMixin';
    import Select2 from '../Select2';

    Vue.use(BootstrapVue);
    Vue.use(GridPlugin)
    Vue.use(VueCurrencyInput);
    Vue.use(NumbersMixin);
    Vue.component('select2', Select2);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);

    //this is required to make the getGroupCaption function available globally and thus within the groupCaptionTemplate
    var paymentsGrid_vueComponent = null;
    function paymentsGrid_getGroupCaption(field, headerText, value) {
        return paymentsGrid_vueComponent.getGroupCaption(field, headerText, value);
    }
    window.paymentsGrid_getGroupCaption = paymentsGrid_getGroupCaption;

    export default {
        name: 'paymentsGrid',
        mixins: [ErrorsMixin, DateTimeMixin, GridMixins, NumbersMixin],
        components: {
            'filter-ui': FilterUI,
        },
        provide: {
            grid: [ColumnChooser, Resize, Sort, Group, Page, Toolbar, Search, ExcelExport, DetailRow]
        },
        props: {
            apiBaseUrl: {
                type: String,
                required: true
            },
            paymentLinkBaseUrl: {
                type: String,
                required: true
            },
            orderLinkBaseUrl: {
                type: String,
                required: true
            },
            canManuallyProcessPayments: {
                type: Boolean,
                default: false
            },
            canRefundPayments: {
                type: Boolean,
                default: false
            },
            canCreatePayments: {
                type: Boolean,
                default: false
            },
            canCancelPayments: {
                type: Boolean,
                default: false
            },
            allPaymentStatusOptions: {
                type: Array,
                default: null
            },
            allCourtRegistryOptions: {
                type: Array,
                default: null
            },
            allInterviewOrganisationOptions: {
                type: Array,
                default: null
            },
            defaultExternalPaymentRequestorId: {
                type: String,
                default: null
            },
            defaultExternalPaymentRequestorAmountIncludesGst: {
                type: Boolean,
                default: null
            },
            defaultExternalPaymentRequestorGstPercentage: {
                type: Number,
                default: null
            },
            // josef new generic props 
            defaultGenericPaymentRequestorId: {
                type: String,
                default: null
            },
            defaultGenericPaymentRequestorAmountIncludesGst: {
                type: Boolean,
                default: null
            },
            defaultGenericPaymentRequestorGstPercentage: {
                type: Number,
                default: null
            }
        },
        data: function () {
            return {
                groupSettings: { showGroupedColumn: true, disablePageWiseAggregates: true, captionTemplate: "${paymentsGrid_getGroupCaption(field, headerText, key)}" },
                pageSettings: { pageSize: 10, pageSizes: [5, 10, 25, 50] },

                allCourtRegistries: null,
                allInterviewOrganisations: null,

                initialized: false,
                filterFields: [
                    {
                        field: "CourtRegistryId",
                        label: "Court/Tribunal/Other",
                        type: "multiselect",
                        options: this.allCourtRegistryOptions,
                        placeholder: "Select Court/Tribunal/Other Values"
                    },
                    {
                        field: "InterviewOrganisationId",
                        label: "Organisation",
                        type: "multiselect",
                        options: this.allInterviewOrganisationOptions,
                        placeholder: "Select Organisations"
                    },
                    {
                        field: "Status",
                        label: "Status",
                        type: "multiselect",
                        options: this.allPaymentStatusOptions,
                        placeholder: "Select Status Values"
                    },
                    {
                        field: "TotalAmount",
                        label: "Total Amount",
                        type: "number"
                    },
                    {
                        field: "CreatedDate",
                        label: "Created/Requested Date",
                        type: "date",
                        format: "dd/MM/yyyy"
                    },
                    {
                        field: "ExpiryDate",
                        label: "Expiration Date",
                        type: "date",
                        format: "dd/MM/yyyy"
                    },
                    {
                        field: "SubmittedDate",
                        label: "Submitted Date",
                        type: "date",
                        format: "dd/MM/yyyy"
                    },
                    {
                        field: "ProcessedOrReceivedDate",
                        label: "Processed/Received Date",
                        type: "date",
                        format: "dd/MM/yyyy"
                    },
                ],
                quickFilters: null,
                initialFilter: null,

                sortSettings: { columns: [{ field: "createdDateTimeUTC", direction: "Descending" }] },
                gridDataState: {
                    filter: null,
                    search: null,
                    sorted: [{ name: "reference", direction: "descending" }],
                    group: null,
                    skip: 0,
                    take: 10,
                    requiresCounts: true
                },
                gridData: { result: [], count: 0 },
                grid: null,

                currentPayment: null,
                showErrors: false,
                currentErrors: null,
                    
                detailTemplate: function () {
                    return { template: PaymentsGridDetailsRow };
                },

                columnFormats: {
                    'amount': {
                        type: 'currency',
                        options: {
                            currencyCodeProperty: 'currencyCode'
                        }
                    },
                    'bankCharges': {
                        type: 'currency',
                        options: {
                            currencyCodeProperty: 'currencyCode'
                        }
                    },
                    'gstPercentage': {
                        type: 'percentage',
                        options: {
                            numberIsPercentage: true
                        }
                    },
                    'gstAmount': {
                        type: 'currency',
                        options: {
                            currencyCodeProperty: 'currencyCode'
                        }
                    },
                    'totalAmount': {
                        type: 'currency',
                        options: {
                            currencyCodeProperty: 'currencyCode'
                        }
                    },
                    'createdDateTimeUTC': {
                        type: 'datetime'
                    },
                    'expiryDateTimeUTC': {
                        type: 'datetime'
                    },
                    'submittedDateTimeUTC': {
                        type: 'datetime'
                    },
                    'processedOrReceivedDateTimeUTC': {
                        type: 'datetime',
                        options: {
                            mode: 'checktime'
                        }
                    },
                    'refundedAmount': {
                        type: 'currency',
                        options: {
                            currencyCodeProperty: 'currencyCode'
                        }
                    }
                },

                newExternalPaymentData: {
                    dialogOpen: false,
                    currentErrors: null,
                    busy: false,
                    paymentRequestorId: null,
                    externalPaymentReference: null,
                    externalOtherReference: null,
                    payerEmail: null,
                    amount: null,
                    totalAmount: null,
                    expiryDate: null,
                    customMessages: {
                        externalPaymentReference: {
                            required: 'Payment Reference is required'
                        },
                        
                        payerEmail: {
                            email: 'Email must be valid'
                        },
                        amount: {
                            required: 'Amount must be ' + this.formatAsCurrency(0.01, this.$config.defaultCurrencyCode) + ' or more',
                            min_value: 'Amount must be ' + this.formatAsCurrency(0.01, this.$config.defaultCurrencyCode) + ' or more',
                        }
                    }
                },


               // newGenericPaymentData.customMessages.genericPaymentReference

                newGenericPaymentData: {
                    dialogOpen: false,
                    currentErrors: null,
                    busy: false,
                    //paymentRequestorId: null,
                    externalPaymentReference: null,
                    matterOrCaseReference: null,
                    courtRegistryId: null,
                    interviewOrganisationId: null,
                    payerEmail: null,
                    payerName: null,
                    amount: null,
                    totalAmount: null,
                    amountIncludesGst: true,
                    dueDateTimeUTC: null,
                    customMessages: {
                        genericPaymentReference: {
                            required: 'Payment Reference is required'
                        },
                        payerName: {
                            required: 'Client name is required'
                        },
                        payerEmail: {
                            required: 'Client email is required',
                            email: 'Email must be valid'
                        },
                        amount: {
                            required: 'Amount must be ' + this.formatAsCurrency(0.01, this.$config.defaultCurrencyCode) + ' or more',
                            min_value: 'Amount must be ' + this.formatAsCurrency(0.01, this.$config.defaultCurrencyCode) + ' or more',
                        },
                        totalAmount: {
                            min_value: 'Total Amount must be ' + this.formatAsCurrency(0.01, this.$config.defaultCurrencyCode) + ' or more'
                        }
                    }
                },

                refundPaymentData: {
                    dialogOpen: false,
                    currentErrors: null,
                    busy: false,
                    paymentId: null,
                    currencyCode: null,
                    maxAmount: null,
                    amount: null,
                    amountIncludesGst: false,
                    gstPercentage: null,
                    validationRules: {
                        amount: {}
                    },
                    customMessages: {
                        amount: {}
                    }
                }
            }
        },
        computed: {
            toolbar: function () {
                var toolbarItems = this.defaultEJ2DataGridToolbarItems({ grouping: false, export: true, columnchooser: true });
                if (this.canManuallyProcessPayments) {
                    toolbarItems =
                        toolbarItems.concat(
                            [
                                { text: "Mark Succeeded", tooltipText: "Mark selected Payment Succeeded", prefixIcon: "fas fa-check", id: "markpaymentsucceeded" },
                                { text: "Mark Failed", tooltipText: "Mark selected Payment Failed", prefixIcon: "fas fa-times", id: "markpaymentfailed" },
                            ]
                        );
                }                       
                if (this.canRefundPayments) {
                    toolbarItems =
                        toolbarItems.concat(
                            [
                                { text: "Refund", tooltipText: "Refund selected Payment", prefixIcon: "fas fa-undo", id: "refundpayment" },
                            ]
                        );
                }
                if (this.canCancelPayments) {
                    toolbarItems =
                        toolbarItems.concat(
                            [
                                { text: "Cancel", tooltipText: "Cancel selected Payment", prefixIcon: "fas fa-plus", id: "cancelpayment" },
                            ]
                        );
                }
                if (this.canCreatePayments && this.defaultExternalPaymentRequestorId) {
                    toolbarItems =
                        toolbarItems.concat(
                            [
                                { text: "New QLD", tooltipText: "New QLD/ServiceNOW Payment", prefixIcon: "fas fa-plus", id: "newexternalpayment" },
                            ]
                        );
                } 
                // josef - new generic payment
                if (this.canCreatePayments) {
                    toolbarItems =
                        toolbarItems.concat(
                            [
                                { text: "New Generic", tooltipText: "New Generic Payment", prefixIcon: "fas fa-plus", id: "newgenericpayment" },
                            ]
                        );
                }

                return toolbarItems;
            }
        },
        methods: {
            interviewOrganisationChanged: function () {
                if (this.newGenericPaymentData.interviewOrganisationId) { this.newGenericPaymentData.courtRegistryId = null; }
            },
            courtRegistryChanged: function () {
                if (this.newGenericPaymentData.courtRegistryId) { this.newGenericPaymentData.interviewOrganisationId = null; }
            },
            resetNewGenericPaymentData: function() {
                this.newGenericPaymentData.currentErrors = null;
                this.newGenericPaymentData.busy = false;
                this.newGenericPaymentData.courtRegistryId = null;
                this.newGenericPaymentData.interviewOrganisationId = null;
                this.newGenericPaymentData.matterOrCaseReference = null;
                this.newGenericPaymentData.genericPaymentReference = null;
                this.newGenericPaymentData.description = null;
                this.newGenericPaymentData.payerEmail = null;
                this.newGenericPaymentData.payerName = null;
                this.newGenericPaymentData.amount = 0.0;
                this.newGenericPaymentData.totalAmount = null;
                this.newGenericPaymentData.dueDateTimeUTC = null;
                this.newGenericPaymentData.amountIncludesGst = true;
            },
            getGroupCaption: function (field, headerText, value) {
                if (field == "createdDateTimeUTC") value = this.dateTimeDisplayString(value, 'includetime', true, false);
                else if (field == "processedOrReceivedDateTimeUTC") value = this.dateTimeDisplayString(value, 'includetime', true, false);
                return headerText + ": " + value;
            },
            customizeGroupKeys(groupedDate) {
                groupedDate.forEach(g => {
                    if (g.field && g.key && g.items) {
                        if (g.field == "createdDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'includetime', true, false);
                        else if (g.field == "processedOrReceivedDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'includetime', true, false);                            
                        this.customizeGroupKeys(g.items);
                    }
                });
            },
            customiseCell: function (args) {
                if ((args.column.field == 'reference') && (args.name == 'queryCellInfo') && args.data.enablePaymentLink) {
                    args.cell.innerHTML = "<a href='" + this.paymentLinkBaseUrl + args.data.paymentId + "' target='_blank'>" + args.data.reference + "</a>";
                }
                else if ((args.column.field == 'orderId') && (args.name == 'queryCellInfo') && args.data.orderId != null) {
                    args.cell.innerHTML = "<a href='" + this.orderLinkBaseUrl + args.data.orderId + "' target='_blank'>" + args.data.orderNumberDisplayText + "</a>";
                }
                this.defaultEJ2DataGridQueryCellInfo(args, this.columnFormats);
            },
            filterChanged: function (filter) {
                this.gridDataState.filter = filter;
                if (this.initialized) this.grid.refresh();
            },

            gridDataStateChanged: function (args) {
                this.gridDataState.search = args.search;
                this.gridDataState.sorted = args.sorted;
                this.gridDataState.group = args.group;
                this.gridDataState.skip = args.skip;
                this.gridDataState.take = args.take;
                this.gridDataState.requiresCounts = args.requiresCounts;
                if (this.initialized) {
                    this.grid.showSpinner();
                    this.loadPayments(this.gridDataState)
                        .then(result => {
                            if (result) {
                                this.gridData = result;
                            }
                            this.grid.hideSpinner();
                        });
                }
            },
            toolbarClicked: function (args) {    
                if ((args.item.id === 'markpaymentsucceeded') ||
                    (args.item.id === 'markpaymentfailed') ||
                    (args.item.id === 'refundpayment') || 
                    (args.item.id === 'cancelpayment') || 
                    (args.item.id === 'newexternalpayment') ||
                    (args.item.id === 'newgenericpayment')) {
                    this.handleAction(args.item.id, this.grid.getSelectedRecords()[0]);
                } else if (args.item.id === 'excelexport') {
                    //to properly export all data (and not just the entries displayed on the current page) we have to load everything first
                    //we do this by taking the current grid state (which includes the current filter and search and sort settings)
                    var gridDataState = {
                        ...this.gridDataState
                    };
                    //but reset the skip and take to 0 which will return everything and unset the requiresCounts cause we are not interseted in the totals
                    gridDataState.requiresCounts = false;
                    this.grid.showSpinner();
                    this.loadPayments(gridDataState)
                        .then(result => {
                            //the result is either false if something failed during the axios request, or it will contain the list in the result property which is what we have to pass to the actual export
                            if (result) {
                                var query = this.grid.getDataModule().generateQuery(true);
                                var groupedData = new DataManager(result.result).executeLocal(query);
                                this.grid.excelExport({ dataSource: groupedData });
                            }
                            this.grid.hideSpinner();
                        });
                } else {
                    this.defaultEJ2DataGridToolbarClickHandler(args, this.grid);
                }
            },
            handleAction: function (action, payment) {

                if (action === 'markpaymentsucceeded') {
                    this.$bvModal.msgBoxConfirm(
                        [
                            this.$createElement(
                                'div',
                                {
                                    domProps:
                                    {
                                        innerHTML: "<p>Are you sure you want to mark the payment as succeeded?</p>"
                                    }
                                }
                            )
                        ],
                        {
                            title: 'Mark Payment Succeeded',
                            size: 'md',
                            okVariant: 'danger',
                            okTitle: 'Yes, Mark Succeeded',
                            cancelTitle: 'No, Keep as Unknown',
                            centered: true
                        })
                        .then(value => {
                            if (value) {
                                this.processPaymentManually(payment.paymentId, true);
                            }
                        });
                }
                else if (action === 'markpaymentfailed') {
                    this.$bvModal.msgBoxConfirm(
                        [
                            this.$createElement(
                                'div',
                                {
                                    domProps:
                                    {
                                        innerHTML: "<p>Are you sure you want to mark the payment as failed?</p>"
                                    }
                                }
                            )
                        ],
                        {
                            title: 'Mark Payment Failed',
                            size: 'md',
                            okVariant: 'danger',
                            okTitle: 'Yes, Mark Failed',
                            cancelTitle: 'No, Keep as Unknown',
                            centered: true
                        })
                        .then(value => {
                            if (value) {
                                this.processPaymentManually(payment.paymentId, false);
                            }
                        });
                }
                else if (action === 'refundpayment') {
                    this.refundPaymentData.currentErrors = null;
                    this.refundPaymentData.busy = false;
                    this.refundPaymentData.paymentId = payment.paymentId;
                    this.refundPaymentData.currencyCode = payment.currencyCode;
                    this.refundPaymentData.maxAmount = payment.maxRefundAmount;
                    this.refundPaymentData.amount = 0.0;
                    this.refundPaymentData.amountIncludesGst = payment.refundAmountIncludesGst;
                    this.refundPaymentData.gstPercentage = payment.gstPercentage;
                    this.refundPaymentData.validationRules = {
                        amount: {
                            required: true,
                            min_value: { min: 0.01 },
                            max_value: { max: payment.maxRefundAmount }
                        }
                    };
                    this.refundPaymentData.customMessages = {
                        amount: {
                            required: 'Amount must be ' + this.formatAsCurrency(0.01, this.refundPaymentData.currencyCode) + ' or more',
                            min_value: 'Amount must be ' + this.formatAsCurrency(0.01, this.refundPaymentData.currencyCode) + ' or more',
                            max_value: 'Amount must be ' + this.formatAsCurrency(this.refundPaymentData.maxAmount, this.refundPaymentData.currencyCode) + ' or less'
                        }
                    };
                    
                    this.refundPaymentData.dialogOpen = true;
                }
                else if (action === 'cancelpayment') {
                    this.$bvModal.msgBoxConfirm(
                        [
                            this.$createElement(
                                'div',
                                {
                                    domProps:
                                    {
                                        innerHTML: "<p>Are you sure you want to mark the payment as cancelled?</p>"
                                    }
                                }
                            )
                        ],
                        {
                            title: 'Mark Payment Cancelled',
                            size: 'md',
                            okVariant: 'danger',
                            okTitle: 'Yes, Mark Cancelled',
                            cancelTitle: 'No, do not Cancel',
                            centered: true
                        })
                        .then(value => {
                            if (value) {
                                this.cancelPayment(payment.paymentId);
                            }
                        });
                }
                else if (action === 'newexternalpayment') {
                    this.newExternalPaymentData.currentErrors = null;
                    this.newExternalPaymentData.busy = false;
                    this.newExternalPaymentData.externalPaymentReference = null;
                    this.newExternalPaymentData.externalOtherReference = null;
                    this.newExternalPaymentData.payerEmail = null;
                    this.newExternalPaymentData.amount = 0.0;
                    this.newExternalPaymentData.totalAmount = null;
                    this.newExternalPaymentData.expiryDate = null;
                    this.newExternalPaymentData.dialogOpen = true;
                }
                else if (action === 'newgenericpayment') {
                    this.resetNewGenericPaymentData()
                    this.newGenericPaymentData.dialogOpen = true;
                }
            },
            rowSelectionChanged: function (args) {
                if (args.name == 'rowSelected') {
                    this.updateToolbarItems(this.grid.getSelectedRecords()[0])
                } else {
                    this.updateToolbarItems(null)
                }
            },
            updateToolbarItems: function (payment) {
                if (this.canManuallyProcessPayments) {
                    this.setToolbarItemState('markpaymentsucceeded', (payment != null) && (payment.enableManualProcessing == true));
                    this.setToolbarItemState('markpaymentfailed', (payment != null) && (payment.enableManualProcessing == true));
                }
                if (this.canRefundPayments) {
                    this.setToolbarItemState('refundpayment', (payment != null) && (payment.enableRefund == true));
                }
                if (this.canCancelPayments) {
                    this.setToolbarItemState('cancelpayment', (payment != null) && (payment.enableCancel == true));
                }
            },
            setToolbarItemState: function (itemId, enabled) {
                this.grid.toolbarModule.enableItems([itemId], enabled);
            },

            loadPayments: function (gridDataState) {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'Payment/list';
                
                return axios
                    .post(url, JSON.stringify(gridDataState), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(response => {
                        var gridData = response.data;
                        if (gridData && gridData.result) {
                            gridData.result = gridData.result.map((payment) => {
                                if (payment.createdDateTimeUTC) payment.createdDateTimeUTC = this.parseDateTime(payment.createdDateTimeUTC, true, true);
                                if (payment.expiryDateTimeUTC) payment.expiryDateTimeUTC = this.parseDateTime(payment.expiryDateTimeUTC, true, true);
                                if (payment.submittedDateTimeUTC) payment.submittedDateTimeUTC = this.parseDateTime(payment.submittedDateTimeUTC, true, true);
                                if (payment.processedOrReceivedDateTimeUTC) payment.processedOrReceivedDateTimeUTC = this.parseDateTime(payment.processedOrReceivedDateTimeUTC, true, true);
                                if (payment.cancelledDateTimeUTC) payment.cancelledDateTimeUTC = this.parseDateTime(payment.cancelledDateTimeUTC, true, true);
                                return payment;
                            });
                        }
                        return gridData;
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                        return false;
                    });
            },           
            processPaymentManually: function (paymentId, paymentSucceeded) { 
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'Payment/';
                url += paymentId;
                url += '/processmanually';
                var data =
                {
                    PaymentSucceeded: paymentSucceeded
                };
                axios
                    .post(url, JSON.stringify(data), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(() => {
                        this.$bvToast.toast('Payment updated successfully', {
                            title: 'Payment Updated',
                            variant: 'success',
                            appendToast: true
                        });
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                    })
                    .finally(() => {
                        this.grid.refresh();
                    });
            },    
            cancelPayment: function (paymentId) { 
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'Payment/';
                url += paymentId;
                url += '/cancel';
                var data =
                {

                };
                axios
                    .post(url, JSON.stringify(data), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(() => {
                        this.$bvToast.toast('Payment cancelled successfully', {
                            title: 'Payment Cancelled',
                            variant: 'success',
                            appendToast: true
                        });
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                    })
                    .finally(() => {
                        this.grid.refresh();
                    });
            },           
            refundPayment: function () {
                this.refundPaymentData.busy = true;
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'Payment/';
                url += this.refundPaymentData.paymentId;
                url += '/fiservrefund';
                var data =
                {
                    amount: this.refundPaymentData.amount
                };
                axios
                    .post(url, JSON.stringify(data), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(result => {
                        this.refundPaymentData.currentErrors = null;
                        this.refundPaymentData.busy = false;
                        this.refundPaymentData.paymentId = null;
                        this.refundPaymentData.currencyCode = null;
                        this.refundPaymentData.maxAmount = null;
                        this.refundPaymentData.amount = null;
                        this.refundPaymentData.amountIncludesGst = false;
                        this.refundPaymentData.gstPercentage = null;
                        this.refundPaymentData.validationRules = {
                            amount: {}
                        };
                        this.refundPaymentData.customMessages = {
                            amount: {}
                        };
                        this.refundPaymentData.dialogOpen = false;

                        if (result.data.success) {
                            this.$bvToast.toast('Refund processed successfully', {
                                title: 'Refund Processed',
                                variant: 'success',
                                appendToast: true
                            });
                        } else if(result.data.errorMessage) {
                            this.$bvToast.toast('Refund processing failed. ' + result.data.errorMessage, {
                                title: 'Refund Processed',
                                variant: 'danger',
                                appendToast: true
                            });
                        } else {
                            this.$bvToast.toast('Refund processing failed.', {
                                title: 'Refund Processed',
                                variant: 'danger',
                                appendToast: true
                            });
                        }
                        
                        this.grid.refresh();
                    })
                    .catch(ex => {
                        this.refundPaymentData.currentErrors = this.handleAxiosException(ex);
                        this.refundPaymentData.busy = false;
                    });
            },
            newExternalPayment: function () {
                this.newExternalPaymentData.busy = true;
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'Payment/external';
                var data =
                {
                    paymentRequestorId: this.defaultExternalPaymentRequestorId,
                    externalPaymentReference: this.newExternalPaymentData.externalPaymentReference,
                    amount: this.newExternalPaymentData.amount
                };
                if (this.newExternalPaymentData.externalOtherReference) {
                    data.externalOtherReference = this.newExternalPaymentData.externalOtherReference;
                }
                if (this.newExternalPaymentData.payerEmail) {
                    data.payerEmail = this.newExternalPaymentData.payerEmail;
                }
                if (this.newExternalPaymentData.totalAmount) {
                    data.totalAmount = this.newExternalPaymentData.totalAmount;
                }
                if (this.newExternalPaymentData.expiryDate) {
                    var expiryDateTime = this.newExternalPaymentData.expiryDate + 'T23:59:59'; //set the expiry time to the end of that day
                    data.expiryDateTimeUTC = this.parseDateTime(expiryDateTime, 'YYYY-MM-DDTHH:mm:ss', false, true);
                }
                axios
                    .post(url, JSON.stringify(data), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(() => {
                        this.newExternalPaymentData.currentErrors = null;
                        this.newExternalPaymentData.busy = false;
                        this.newExternalPaymentData.externalPaymentReference = null;
                        this.newExternalPaymentData.externalOtherReference = null;
                        this.newExternalPaymentData.payerEmail = null;
                        this.newExternalPaymentData.amount = 0.0;
                        this.newExternalPaymentData.totalAmount = null;
                        this.newExternalPaymentData.expiryDate = null;
                        this.newExternalPaymentData.dialogOpen = false;

                        this.$bvToast.toast('New Payment created successfully', {
                            title: 'Payment Created',
                            variant: 'success',
                            appendToast: true
                        });

                        this.grid.refresh();
                    })
                    .catch(ex => {
                        this.newExternalPaymentData.currentErrors = this.handleAxiosException(ex);
                        this.newExternalPaymentData.busy = false;
                    });
            },
            newGenericPayment: function () {
                this.newGenericPaymentData.busy = true;
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'Payment/generic';

                // required fields
                var data =
                {
                    genericPaymentReference: this.newGenericPaymentData.genericPaymentReference,
                    amount: this.newGenericPaymentData.amount,
                    payerName: this.newGenericPaymentData.payerName,
                    payerEmail: this.newGenericPaymentData.payerEmail,
                    amountIncludesGST: this.newGenericPaymentData.amountIncludesGst 
                };
               
                //optional fields
                if (this.newGenericPaymentData.courtRegistryId) {
                    data.courtRegistryId = this.newGenericPaymentData.courtRegistryId;
                }
                if (this.newGenericPaymentData.interviewOrganisationId) {
                    data.interviewOrganisationId = this.newGenericPaymentData.interviewOrganisationId;
                }
                if (this.newGenericPaymentData.matterOrCaseReference) {
                    data.matterOrCaseReference = this.newGenericPaymentData.matterOrCaseReference;
                }
                if (this.newGenericPaymentData.description) {
                    data.description = this.newGenericPaymentData.description;
                }
                if (this.newGenericPaymentData.dueDateTimeUTC) {
                    var dueDateTimeUTC = this.newGenericPaymentData.dueDateTimeUTC + 'T23:59:59'; //set the expiry time to the end of that day
                    data.dueDateTimeUTC = this.parseDateTime(dueDateTimeUTC, 'YYYY-MM-DDTHH:mm:ss', false, true);
                }
                axios
                    .post(url, JSON.stringify(data), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(() => {
                        this.resetNewGenericPaymentData();
                        this.newGenericPaymentData.dialogOpen = false;

                        this.$bvToast.toast('New generic Payment created successfully', {
                            title: 'Payment Created',
                            variant: 'success',
                            appendToast: true
                        });

                        this.grid.refresh();
                    })
                    .catch(ex => {
                        this.newGenericPaymentData.currentErrors = this.handleAxiosException(ex);
                        this.newGenericPaymentData.busy = false;
                    });
            },

            loadAllCourtRegistries: function () {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'courtregistry/all';

                axios
                    .get(url)
                    .then(response => {
                        this.allCourtRegistries = response.data;
                        console.log(this.allCourtRegistries);
                    })
                    .catch(ex => {
                        this.newGenericePaymentData.currentErrors = this.handleAxiosException(ex);
                        this.newGenericPaymentData.busy = false;
                    });
            },

            loadAllInterviewOrganisations: function () {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'intervieworganisation/all';

                axios
                    .get(url)
                    .then(response => {
                        this.allInterviewOrganisations = response.data;
                        console.log(this.allInterviewOrganisations);
                    })
                    .catch(ex => {
                        this.newGenericePaymentData.currentErrors = this.handleAxiosException(ex);
                        this.newGenericPaymentData.busy = false;
                    });
            },
            gridCreated: function () {
                Vue.nextTick(() => {
                    this.defaultEJ2DataGridCreatedHandler(this.grid);
                });
            }
        },

        mounted: function () {
            paymentsGrid_vueComponent = this;
            this.grid = this.$refs.paymentsGrid.ej2Instances;
            this.setupGridPersistence(this.grid);
            this.updateToolbarItems(this.grid, null);
            this.$refs.paymentFilter.loadQuickFilters()
                .then(() => {
                    this.initialized = true;
                    setTimeout(() => {
                        this.grid.refresh();
                    }, 250);
                });
            this.loadAllCourtRegistries();
            this.loadAllInterviewOrganisations();
        }
    }
</script>
